import { Check, DoorOpen, Hand, Mic, MicOff, PhoneCall, Search, X } from "lucide-react";
import { useParticipants, useTracks } from "@livekit/components-react";
import { Track } from "livekit-client";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { parseHandRaised } from "../lib/handRaiseMetadata";
import type { LobbyParticipant } from "../api/types/video";
import type { OutgoingRing } from "../hooks/useOutgoingRings";
import {
  ORG_DIRECTORY_MIN_QUERY_LENGTH,
  useOrgDirectorySearch,
} from "../hooks/useOrgDirectorySearch";
import { useMe } from "../../chat/hooks/useChatRole";
import { videoService } from "../api/video.api";
import { useParticipantRole, type ParticipantRole } from "../hooks/useParticipantRole";
import { RoleBadge } from "./RoleBadge";

interface InCallParticipantsContentProps {
  waitingParticipants?: LobbyParticipant[];
  onApprove?: (id: string) => void;
  onDeny?: (id: string) => void;
  /** Colleagues rung in who have not yet joined the LiveKit room. */
  invited?: OutgoingRing[];
  canRing?: boolean;
  ringingSub?: string | null;
  onRing?: (user: { sub: string; name: string }) => void;
  /** Backend room UUID — required to call the promote/demote endpoint (#215). */
  roomId?: string;
  /** Whether the local user has the "admin" session role — shows the
   *  per-participant role selector (#215). */
  isAdmin?: boolean;
}

/** Roles an admin can assign. A meeting twin's role is set by the service, not
 *  picked here, and `setParticipantRole` does not accept it. */
type AssignableRole = Exclude<ParticipantRole, "twin">;

const ROLE_OPTIONS: AssignableRole[] = ["admin", "user", "guest"];

/** Initials disc at the design's roster avatar size (`Avatar size={34}`). */
function InitialAvatar({ name, ring }: { name: string; ring?: "ringing" | "accepted" | "declined" }) {
  return (
    <span className="relative shrink-0">
      <span className="size-[34px] rounded-full flex items-center justify-center text-xs font-bold bg-neutral-600 text-white select-none">
        {(name.trim()[0] ?? "?").toUpperCase()}
      </span>
      {ring === "ringing" && <span className="absolute inset-0 rounded-full ring-2 ring-primary anim-pulse-rec" />}
      {ring === "accepted" && <span className="absolute inset-0 rounded-full ring-2 ring-green-500" />}
      {ring === "declined" && <span className="absolute inset-0 rounded-full ring-2 ring-red-400" />}
    </span>
  );
}

const sectionLabel = "px-1 mb-2 text-[10px] font-black uppercase tracking-widest text-muted-foreground";

/** Per-participant Admin/User/Guest selector (#215), shown only to admins.
 *  A plain native `<select>` — no popover machinery needed for three options,
 *  and it keeps keyboard/screen-reader semantics for free. */
function RoleSelect({
  roomId,
  identity,
  name,
  role,
}: {
  roomId: string;
  identity: string;
  name: string;
  role: ParticipantRole;
}) {
  const { t } = useTranslation();
  const [pending, setPending] = useState(false);

  const handleChange = (next: AssignableRole) => {
    if (next === role) return;
    setPending(true);
    videoService
      .setParticipantRole(roomId, identity, next)
      .catch((err) => {
        console.error("setParticipantRole failed:", err);
        toast.error(t("call.controls.setRoleError", { name }));
      })
      .finally(() => setPending(false));
  };

  return (
    <select
      value={role}
      disabled={pending}
      onChange={(e) => handleChange(e.target.value as AssignableRole)}
      aria-label={t("call.controls.roleSelectLabel", { name })}
      className="h-7 rounded-lg border border-border bg-muted/40 px-1.5 text-[11px] font-medium text-foreground outline-none focus:border-primary disabled:opacity-50"
    >
      {ROLE_OPTIONS.map((opt) => (
        <option key={opt} value={opt}>
          {t(`call.role.${opt}`)}
        </option>
      ))}
    </select>
  );
}

/** Per-participant role picker is hidden for now (product request, 2026-10-04).
 *  `RoleSelect` and the set-role endpoint are untouched — flip this back to
 *  `true` to bring the control back. The read-only `RoleBadge` stays. */
const SHOW_ROLE_SELECT = false;

/** One roster row (#215): reads the participant's live role via
 *  `useParticipantRole` — split into its own component because hooks can't
 *  be called from inside `Array.map`'s callback (one hook call per rendered
 *  component instance, not per loop iteration, keeps rules-of-hooks intact). */
function ParticipantRow({
  participant: p,
  micOn,
  speaking,
  handRaised,
  roomId,
  isAdmin,
}: {
  participant: ReturnType<typeof useParticipants>[number];
  micOn: boolean;
  speaking: boolean;
  handRaised: boolean;
  roomId?: string;
  isAdmin: boolean;
}) {
  const { t } = useTranslation();
  const role = useParticipantRole(p);
  const name = p.isLocal ? t("call.controls.you") : p.name || p.identity;

  return (
    <div className="flex items-center gap-3 px-1 py-2">
      <InitialAvatar name={p.name || p.identity || "?"} />
      <div className="flex-1 min-w-0 flex items-center gap-2">
        <span className="text-foreground text-sm font-bold truncate leading-tight">{name}</span>
        {p.isLocal && (
          <span className="text-[10px] font-black uppercase tracking-widest text-muted-foreground shrink-0">
            {t("call.participants.youBadge")}
          </span>
        )}
        <RoleBadge role={role} className="shrink-0" />
        {handRaised && (
          <Hand
            className="size-3.5 text-primary shrink-0"
            aria-label={t("call.participants.handRaised")}
          />
        )}
      </div>
      <div className="flex items-center gap-1.5 shrink-0">
        {SHOW_ROLE_SELECT && isAdmin && roomId && !p.isLocal && (
          <RoleSelect roomId={roomId} identity={p.identity} name={name} role={role} />
        )}
        {speaking ? (
          <span className="flex items-center gap-1 text-primary" aria-label={t("call.participants.micOn")}>
            <span className="size-1.5 rounded-full bg-primary anim-pulse-rec" aria-hidden />
            <Mic className="size-3.5" />
          </span>
        ) : micOn ? (
          <Mic className="size-[15px] text-muted-foreground/50" aria-label={t("call.participants.micOn")} />
        ) : (
          <MicOff className="size-[15px] text-red-400" aria-label={t("call.participants.micOff")} />
        )}
      </div>
    </div>
  );
}

export function InCallParticipantsContent({
  waitingParticipants = [],
  onApprove,
  onDeny,
  invited = [],
  canRing = false,
  ringingSub = null,
  onRing,
  roomId,
  isAdmin = false,
}: InCallParticipantsContentProps) {
  const { t } = useTranslation();
  const participants = useParticipants();
  const tracks = useTracks(
    [{ source: Track.Source.Microphone, withPlaceholder: true }],
    { onlySubscribed: false },
  );
  const [search, setSearch] = useState("");
  const { data: me } = useMe();
  const directory = useOrgDirectorySearch(canRing ? search : "");

  const joinedSubs = useMemo(
    () => new Set(participants.map((p) => p.identity)),
    [participants],
  );
  const invitedSubs = useMemo(() => new Set(invited.map((row) => row.sub)), [invited]);

  const sorted = useMemo(() => {
    return [...participants].sort((a, b) => {
      if (a.isLocal === b.isLocal) return a.identity.localeCompare(b.identity);
      return a.isLocal ? -1 : 1;
    });
  }, [participants]);

  const query = search.trim().toLowerCase();

  const filteredWaiting = useMemo(() => {
    if (!query) return waitingParticipants;
    return waitingParticipants.filter((p) => p.username.toLowerCase().includes(query));
  }, [waitingParticipants, query]);

  const filteredInvited = useMemo(() => {
    if (!query) return invited;
    return invited.filter((row) => row.name.toLowerCase().includes(query));
  }, [invited, query]);

  const filtered = useMemo(() => {
    if (!query) return sorted;
    return sorted.filter((p) => (p.name || p.identity || "").toLowerCase().includes(query));
  }, [sorted, query]);

  const directoryHits = useMemo(() => {
    if (!canRing || search.trim().length < ORG_DIRECTORY_MIN_QUERY_LENGTH) return [];
    return (directory.data?.results ?? []).filter(
      (u) =>
        u.sub !== me?.sub &&
        !joinedSubs.has(u.sub) &&
        !invitedSubs.has(u.sub),
    );
  }, [canRing, search, directory.data, me?.sub, joinedSubs, invitedSubs]);

  const showDirectoryEmpty =
    canRing &&
    search.trim().length >= ORG_DIRECTORY_MIN_QUERY_LENGTH &&
    !directory.isFetching &&
    Boolean(directory.data) &&
    directoryHits.length === 0 &&
    filtered.length === 0 &&
    filteredWaiting.length === 0 &&
    filteredInvited.length === 0;

  // Colleagues being rung right now sit in their own "Ringing…" group, as in
  // the design; anyone else invited (declined, missed) can be rung again.
  const ringingRows = filteredInvited.filter((row) => row.status === "ringing" || row.status === "accepted");
  const pendingRows = filteredInvited.filter((row) => row.status !== "ringing" && row.status !== "accepted");

  const ringButton = (target: { sub: string; name: string }, disabled: boolean, primary: boolean) => (
    <button
      type="button"
      disabled={disabled}
      onClick={() => onRing?.(target)}
      aria-label={t("call.ringIn.ringPerson", { name: target.name })}
      title={t("call.ringIn.ringPerson", { name: target.name })}
      className={`size-8 shrink-0 rounded-lg flex items-center justify-center focus:outline-none focus-visible:ring-2 focus-visible:ring-primary disabled:opacity-50 ${
        primary
          ? "bg-primary text-primary-foreground hover:scale-105 transition-transform"
          : "bg-muted text-muted-foreground hover:bg-primary hover:text-primary-foreground transition-colors"
      }`}
    >
      <PhoneCall className="size-[15px]" />
    </button>
  );

  return (
    <div className="flex flex-col h-full min-h-0">
      <div className="px-3 pt-3 shrink-0">
        <div className="relative">
          <Search className="absolute start-3 top-1/2 -translate-y-1/2 size-3.5 text-muted-foreground pointer-events-none" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t("call.controls.searchPeoplePlaceholder")}
            aria-label={t("call.controls.searchPeoplePlaceholder")}
            className="w-full h-9 ps-9 pe-3 bg-muted/40 border border-border rounded-xl text-sm outline-none focus:border-primary transition-colors text-foreground"
          />
        </div>
      </div>

      <div className="flex-1 min-h-0 overflow-y-auto no-scrollbar">
        {ringingRows.length > 0 && (
          <div className="px-3 pt-3">
            <div className={sectionLabel}>{t("call.ringIn.ringing")}</div>
            {ringingRows.map((row) => (
              <div key={row.inviteId} className="flex items-center gap-3 px-1 py-2 anim-fade">
                <InitialAvatar name={row.name} ring={row.status === "accepted" ? "accepted" : "ringing"} />
                <div className="flex-1 min-w-0">
                  <p className="text-foreground text-sm font-bold truncate leading-tight">{row.name}</p>
                  {row.status === "accepted" ? (
                    <p className="text-green-600 text-[11px] font-medium flex items-center gap-1">
                      <Check className="size-[11px]" /> {t("call.ringIn.rowAccepted", { defaultValue: "accepted, joining…" })}
                    </p>
                  ) : (
                    <p className="text-primary text-[11px] font-medium flex items-center gap-1">
                      <PhoneCall className="size-[11px]" /> {t("call.ringIn.rowCalling", { defaultValue: "calling…" })}
                    </p>
                  )}
                </div>
                {row.status === "accepted" && (
                  <span className="size-8 shrink-0 rounded-lg bg-green-500/10 text-green-600 flex items-center justify-center">
                    <Check className="size-[15px]" />
                  </span>
                )}
              </div>
            ))}
            <div className="h-px bg-border mx-1 mt-2" />
          </div>
        )}

        {filteredWaiting.length > 0 && (
          <div className="px-3 pt-3">
            <div className="flex items-center gap-2 px-1 mb-2">
              <DoorOpen className="size-3.5 text-primary" />
              <span className="text-[10px] font-black uppercase tracking-widest text-primary">
                {t("call.controls.waitingRoomSection")}
              </span>
              <span className="ms-auto size-5 rounded-full bg-primary text-primary-foreground text-[11px] font-black flex items-center justify-center">
                {filteredWaiting.length}
              </span>
            </div>
            {filteredWaiting.map((p) => (
              <div key={p.id} className="flex items-center gap-3 px-1 py-2 anim-fade">
                <InitialAvatar name={p.username} />
                <div className="flex-1 min-w-0">
                  <p className="text-foreground text-sm font-bold truncate leading-tight">{p.username}</p>
                  <p className="text-muted-foreground text-[11px] font-medium">
                    {p.company ?? t("call.controls.wantsToJoin")}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => onDeny?.(p.id)}
                  aria-label={t("call.participants.rejectAriaLabel", { username: p.username })}
                  title={t("call.participants.reject")}
                  className="size-8 shrink-0 rounded-lg bg-muted text-muted-foreground hover:bg-red-500 hover:text-white flex items-center justify-center transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary"
                >
                  <X className="size-[15px]" />
                </button>
                <button
                  type="button"
                  onClick={() => onApprove?.(p.id)}
                  aria-label={t("call.participants.approveAriaLabel", { username: p.username })}
                  title={t("call.participants.approve")}
                  className="size-8 shrink-0 rounded-lg bg-primary text-primary-foreground hover:scale-105 flex items-center justify-center transition-transform focus:outline-none focus-visible:ring-2 focus-visible:ring-primary"
                >
                  <Check className="size-[15px]" />
                </button>
              </div>
            ))}
            {filteredWaiting.length > 1 && (
              <div className="flex items-center gap-2 pt-1 pb-1">
                <button
                  type="button"
                  onClick={() => filteredWaiting.forEach((p) => onDeny?.(p.id))}
                  className="flex-1 h-8 rounded-xl text-muted-foreground hover:bg-muted text-xs font-bold transition-colors"
                >
                  {t("call.controls.denyAll")}
                </button>
                <button
                  type="button"
                  onClick={() => filteredWaiting.forEach((p) => onApprove?.(p.id))}
                  className="flex-1 h-8 rounded-xl bg-foreground/[0.06] border border-border text-foreground hover:bg-foreground/10 text-xs font-bold transition-colors"
                >
                  {t("call.controls.admitAll")}
                </button>
              </div>
            )}
            <div className="h-px bg-border mx-1 mt-2" />
          </div>
        )}

        {pendingRows.length > 0 && (
          <div className="px-3 pt-3">
            <div className={sectionLabel}>{t("call.controls.invitedNotJoined")}</div>
            {pendingRows.map((row) => (
              <div key={row.inviteId} className="flex items-center gap-3 px-1 py-2">
                <InitialAvatar name={row.name} ring={row.status === "declined" ? "declined" : undefined} />
                <span className="flex-1 min-w-0 text-foreground text-sm font-bold truncate leading-tight">
                  {row.name}
                </span>
                {ringButton({ sub: row.sub, name: row.name }, ringingSub === row.sub, false)}
              </div>
            ))}
            <div className="h-px bg-border mx-1 mt-2" />
          </div>
        )}

        <div className="px-3 py-3">
          <div className={sectionLabel}>{t("call.controls.inTheMeeting")}</div>
          {sorted.length === 0 && (
            <p className="px-1 py-2 text-xs text-muted-foreground">{t("call.participants.onlyYou")}</p>
          )}
          {sorted.length > 0 &&
            filtered.length === 0 &&
            filteredWaiting.length === 0 &&
            filteredInvited.length === 0 &&
            directoryHits.length === 0 && (
              <p className="px-1 py-4 text-center text-xs text-muted-foreground">
                {showDirectoryEmpty
                  ? t("call.ringIn.noResults")
                  : t("call.participants.noMatches", { query: search.trim() })}
              </p>
            )}
          {filtered.map((p) => {
            const micTrack = tracks.find(
              (tr) => tr.participant.sid === p.sid && tr.source === Track.Source.Microphone,
            );
            const micOn = micTrack?.publication ? !micTrack.publication.isMuted : false;
            const speaking = Boolean(p.isSpeaking && micOn);
            const handRaised = parseHandRaised(p.metadata);

            return (
              <ParticipantRow
                key={p.sid}
                participant={p}
                micOn={micOn}
                speaking={speaking}
                handRaised={handRaised}
                roomId={roomId}
                isAdmin={isAdmin}
              />
            );
          })}
        </div>

        {/* Ringing colleagues in from the org directory (search-driven). */}
        {directoryHits.length > 0 && (
          <div className="px-3 pb-3">
            <div className="h-px bg-border mx-1 mb-3" />
            <div className={sectionLabel}>{t("call.controls.inYourOrganization", { defaultValue: "In your organization" })}</div>
            {directoryHits.map((u) => {
              const name = u.name || u.email;
              return (
                <div key={u.sub} className="flex items-center gap-3 px-1 py-2">
                  <InitialAvatar name={name} />
                  <div className="flex-1 min-w-0">
                    <p className="text-foreground text-sm font-bold truncate leading-tight">{name}</p>
                    {u.name && u.email ? (
                      <p className="text-[11px] font-medium text-muted-foreground truncate">{u.email}</p>
                    ) : null}
                  </div>
                  {ringButton({ sub: u.sub, name }, ringingSub === u.sub, true)}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
